import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Heart, MessageCircle, Users, Send, Sparkles, 
  ListChecks, Loader2, RefreshCw, Crown, Info,
  Brain, Activity, Moon, TrendingUp
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SavedAssessment } from '@/hooks/useRelationshipAssessment';
import { createSSELineBuffer, processSSEChunk, flushAndProcessSSE } from '@/lib/sseParser';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ReflectionPrompt {
  question: string;
  dimension: string;
  forWhom: 'both' | 'user' | 'partner';
  purpose: string;
  userAnswer?: string;
  partnerAnswer?: string;
}

interface CouplesReflectionSessionProps {
  userAssessment: SavedAssessment | null;
  partnerAssessment: SavedAssessment | null;
  partnerName?: string;
  onClose?: () => void;
}

export function CouplesReflectionSession({ 
  userAssessment, 
  partnerAssessment, 
  partnerName = 'Partner',
  onClose 
}: CouplesReflectionSessionProps) {
  const [activeTab, setActiveTab] = useState<'live' | 'async'>('live');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [prompts, setPrompts] = useState<ReflectionPrompt[]>([]);
  const [isGeneratingPrompts, setIsGeneratingPrompts] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [aiTier, setAiTier] = useState<'standard' | 'premium'>('standard');
  const [showInfo, setShowInfo] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize with a welcome message and analysis
  useEffect(() => {
    if (userAssessment || partnerAssessment) {
      loadInitialAnalysis();
    }
  }, [userAssessment, partnerAssessment]);

  const loadInitialAnalysis = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('couples-reflection', {
        body: {
          mode: 'analyze_comparison',
          userAssessment,
          partnerAssessment,
        }
      });

      if (error) throw error;
      
      setAnalysis(data.analysis);
      setAiTier(data.aiTier || 'standard');
      
      // Set initial assistant message
      const tierNote = data.aiTier === 'premium' 
        ? "\n\n✨ *Premium AI active - I can see patterns in your sleep, mood, biometrics, and cycle data to provide deeper relationship insights.*"
        : "";
      
      setMessages([{
        role: 'assistant',
        content: `Welcome to your couples reflection session! 💕\n\n${data.analysis}${tierNote}\n\nI'm here to help you both understand each other better. You can ask me questions, explore specific areas of your relationship, or I can suggest conversation starters. What would you like to explore first?`
      }]);
    } catch (error) {
      console.error('Error loading analysis:', error);
      setMessages([{
        role: 'assistant',
        content: `Welcome to your couples reflection session! 💕 I'm here to help you both explore your relationship assessment results together. What aspect of your relationship would you like to discuss?`
      }]);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: inputValue };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/couples-reflection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            mode: 'live_chat',
            userAssessment,
            partnerAssessment,
            messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          toast({
            title: "Rate limit reached",
            description: "Please wait a moment before sending another message.",
            variant: "destructive"
          });
          return;
        }
        if (response.status === 402) {
          toast({
            title: "Credits depleted",
            description: "Please add AI credits to continue.",
            variant: "destructive"
          });
          return;
        }
        throw new Error('Failed to get response');
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      const sseBuf = createSSELineBuffer();

      if (reader) {
        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
        let streamDone = false;

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) {
            const final = flushAndProcessSSE(sseBuf);
            assistantContent += final.texts.join('');
            break;
          }

          const { texts, done: isDone } = processSSEChunk(sseBuf, decoder.decode(value, { stream: true }));
          if (texts.length > 0) {
            assistantContent += texts.join('');
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
              return updated;
            });
          }
          if (isDone) { streamDone = true; break; }
        }

        // Remove empty assistant message if no content
        if (!assistantContent.trim()) {
          setMessages(prev => prev.filter(m => m.content !== ''));
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to get a response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateReflectionPrompts = async () => {
    setIsGeneratingPrompts(true);
    try {
      const { data, error } = await supabase.functions.invoke('couples-reflection', {
        body: {
          mode: 'generate_prompts',
          userAssessment,
          partnerAssessment,
        }
      });

      if (error) throw error;
      
      setPrompts(data.prompts || []);
    } catch (error) {
      console.error('Error generating prompts:', error);
      toast({
        title: "Error",
        description: "Failed to generate reflection prompts. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingPrompts(false);
    }
  };

  const suggestedQuestions = [
    "What does emotional safety mean to each of us?",
    "How can we improve our communication?",
    "What makes us feel most connected?",
    "How do we handle conflict differently?",
  ];

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-secondary/10">
              <Heart className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                AI Couples Reflection
                {aiTier === 'premium' && (
                  <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground gap-1">
                    <Crown className="w-3 h-3" />
                    Premium
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Work through your assessment results together with Therappi guidance
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {partnerAssessment && (
              <Badge variant="secondary" className="gap-1">
                <Users className="w-3 h-3" />
                Both assessments
              </Badge>
            )}
            <Badge variant="outline" className="gap-1 text-xs">
              <Sparkles className="w-3 h-3" />
              3 credits/message
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Info Alert - What this does */}
        {showInfo && (
          <Alert className="bg-muted/50">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <div className="flex justify-between items-start">
                <div>
                  <strong>What this session provides:</strong>
                  <ul className="mt-1 space-y-1 text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Brain className="w-3 h-3" /> Compare perspectives &amp; understand differences
                    </li>
                    <li className="flex items-center gap-2">
                      <MessageCircle className="w-3 h-3" /> Guided dialogue to improve communication
                    </li>
                    <li className="flex items-center gap-2">
                      <TrendingUp className="w-3 h-3" /> Actionable exercises to strengthen your bond
                    </li>
                    {aiTier === 'premium' && (
                      <>
                        <li className="flex items-center gap-2 text-amber-600">
                          <Moon className="w-3 h-3" /> Sleep &amp; recovery impact on relationship
                        </li>
                        <li className="flex items-center gap-2 text-amber-600">
                          <Activity className="w-3 h-3" /> Biometric patterns affecting emotional availability
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs"
                  onClick={() => setShowInfo(false)}
                >
                  Dismiss
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'live' | 'async')}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="live" className="gap-2">
              <MessageCircle className="w-4 h-4" />
              Live Guided Chat
            </TabsTrigger>
            <TabsTrigger value="async" className="gap-2">
              <ListChecks className="w-4 h-4" />
              Reflection Prompts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="space-y-4">
            {/* Chat Messages */}
            <ScrollArea className="h-[400px] rounded-lg border p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-3 h-3 text-secondary" />
                          <span className="text-xs font-medium text-secondary">
                            Therappi® {aiTier === 'premium' && '✨'}
                          </span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-lg p-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Suggested Questions */}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setInputValue(question);
                    }}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="flex gap-2">
              <Textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Share your thoughts..."
                className="min-h-[50px] sm:min-h-[60px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <Button 
                onClick={sendMessage} 
                disabled={isLoading || !inputValue.trim()}
                size="icon"
                className="h-[50px] sm:h-[60px] w-10 shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="async" className="space-y-4">
            {prompts.length === 0 ? (
              <Card className="p-6 text-center">
                <ListChecks className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Generate Reflection Prompts</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create personalised questions based on your assessment results. 
                  Each partner can answer separately, then compare responses.
                  {aiTier === 'premium' && (
                    <span className="block mt-1 text-amber-600">
                      ✨ Premium: Prompts will incorporate your timeline patterns
                    </span>
                  )}
                </p>
                <Button 
                  onClick={generateReflectionPrompts}
                  disabled={isGeneratingPrompts}
                >
                  {isGeneratingPrompts ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Prompts (3 credits)
                    </>
                  )}
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium">Your Reflection Prompts</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={generateReflectionPrompts}
                    disabled={isGeneratingPrompts}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${isGeneratingPrompts ? 'animate-spin' : ''}`} />
                    Regenerate
                  </Button>
                </div>

                {prompts.map((prompt, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Badge variant="outline" className="text-xs">
                        {prompt.dimension}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {prompt.forWhom === 'both' ? 'Both partners' : 
                         prompt.forWhom === 'user' ? 'You' : partnerName}
                      </Badge>
                    </div>
                    <p className="font-medium mb-2">{prompt.question}</p>
                    <p className="text-xs text-muted-foreground">{prompt.purpose}</p>
                    
                    <div className="mt-3 space-y-2">
                      <Textarea
                        placeholder="Write your reflection here..."
                        value={prompt.userAnswer || ''}
                        onChange={(e) => {
                          const updated = [...prompts];
                          updated[index] = { ...prompt, userAnswer: e.target.value };
                          setPrompts(updated);
                        }}
                        className="min-h-[80px] resize-none"
                      />
                    </div>
                  </Card>
                ))}

                <Card className="p-4 bg-muted/50">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium mb-1">Share with your partner</p>
                      <p className="text-xs text-muted-foreground">
                        Have your partner complete their own reflection prompts, then 
                        compare your answers together for deeper understanding.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
